import type { RowEdit } from "@models/job";

const STORAGE_KEY_PREFIX = "xplatform-mapping:edits:";

const storageKey = (jobId: string): string => `${STORAGE_KEY_PREFIX}${jobId}`;

/** One persisted edit. `RowEdit` is a union (a correction carries a rank, a
 *  rejection carries none), and an interface cannot extend a union — so the
 *  row index is intersected on instead of inherited. */
type StoredEdit = RowEdit & { rowIndex: number };

export const loadEditsFromStorage = (jobId: string): Map<number, RowEdit> => {
  if (typeof window === "undefined") return new Map();
  try {
    const raw = window.localStorage.getItem(storageKey(jobId));
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as StoredEdit[];
    if (!Array.isArray(parsed)) return new Map();
    const map = new Map<number, RowEdit>();
    for (const entry of parsed) {
      if (typeof entry.rowIndex !== "number") continue;
      // Rebuilt through the union rather than field-by-field: localStorage is
      // untrusted input, and a stored `{corrected}` with no rank is exactly
      // the shape the type now forbids.
      const { rowIndex, ...edit } = entry;
      map.set(rowIndex, edit);
    }
    return map;
  } catch {
    return new Map();
  }
};

export const persistEditsToStorage = (
  jobId: string,
  edits: Map<number, RowEdit>,
): void => {
  if (typeof window === "undefined") return;
  try {
    if (edits.size === 0) {
      window.localStorage.removeItem(storageKey(jobId));
      return;
    }
    const payload: StoredEdit[] = Array.from(edits.entries()).map(
      ([rowIndex, edit]) => ({ rowIndex, ...edit }),
    );
    window.localStorage.setItem(storageKey(jobId), JSON.stringify(payload));
  } catch {
    /* empty */
  }
};
